import { buildingGroundZ } from "building-generation/elements/level/buildingGroundZ";
import { bottomLevel, levelIsBasement, levelName, sortedLevels } from "building-generation/elements/level/levelTools";
import {
  convertRoofFaceInfoToFaceListArray,
  getRoofsTopFacesByLevel,
  ONLY_DORMER_FACE_TYPE,
} from "building-generation/elements/roofs";
import { checkWallRooms } from "building-generation/elements/walls/wall";
import { getWallPath } from "building-generation/elements/walls/wall";
import { solidWallSubstrateLayer } from "building-generation/elements/walls/wallLayers";
import { getWindowHeight, windowHeadHeight } from "building-generation/elements/window/windowHeightTools";
import { TextBackground } from "building-types/annotation/annotationTypesEnum";
import type { Building } from "building-types/building/buildingTypes";
import { roomEntityHandleForSelectingReportingUnit } from "building-types/handles/roomEntityHandleForSelectingReportingUnit";
import type { Level } from "building-types/levels/levelTypes";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { RoofFace } from "building-types/roofs/roofFace";
import type { Room } from "building-types/rooms/roomTypes";
import type { RoofElevationPlanSettings } from "building-types/settings/roofPlanSettings";
import { ElevationNotePlacementType, GableRoofType, PlanLineStyle } from "building-types/settings/settingsTypesEnum";
import type { ElevationNote } from "building-types/viewNotes/elevationNoteTypes";
import type { ElevationAnnotationParams } from "building-types/views/elevationAnnotationTypes";
import { ElevationNoteEntityDebugMode, LevelAnnotationMethod } from "building-types/views/elevationAnnotationTypes";
import { GradeLineSource } from "building-types/views/GradeLineSource";
import { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import type { SheetGeometry, SheetPolylineGeometry, SheetTextGeometry } from "building-types/views/sheetGeometryTypes";
import {
  SheetGeometryType,
  sheetPolylineGeometryNew,
  sheetTextGeometryNew,
} from "building-types/views/sheetGeometryTypes";
import { DimensionPrecision } from "building-types/views/viewTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import colors from "common/constants/colorsOld";
import { ONE_INCH, ZERO_INCHES } from "common/constants/scaleFactors";
import type { Table } from "common/data/table";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { assert, assertIsNotUndefined, assertIsNumber, ensureDefined } from "common/utils/assertOfType";
import {
  numAdd,
  numDiv,
  numMin,
  numMul,
  numSub,
  percentNew,
  sheetInchesNew,
  sheetPointsNew,
  sheetPointsToInches,
} from "common/utils/nominalNumbers";
import { objectTypedKeys } from "common/utils/objectTypedKeys";
import { PlacementXAnchor, PlacementYAnchor, TextAlignment, TextVerticalAlignment } from "common/utils/textAlignment";
import { formatWithPrecision } from "doc/dimensions";
import { floorAssemblyBaseOffset, floorAssemblyTop } from "doc/elements/floor/floorAssembly";
import { getMergedWindowSettings } from "doc/elements/window/getMergedWindowSettings";
import { modelSpaceToPaperSpaceTransform, paperSpaceToModelSpaceTransform } from "doc/pdf/drawingLayout";
import { DIM_LW } from "doc/plans/constants";
import { DRAWING_LAYOUT, type DrawingLayout } from "doc/plans/layout";
import { makeSheetGeometryFromElevationViewAnnotations } from "doc/plans/planAnnotations";
import type { PlanGeometryBuilderWithCollider } from "doc/plans/planGeometrySource";
import { PlanGeometryBuilder, supplyGeometryTagIfMissing } from "doc/plans/planGeometrySource";
import type {
  PlanGeometryGenerationDiagnostic,
  PlanGeometryGeneratorResult,
  TaggedPlanGeometry,
} from "doc/plans/planGeometrySourceTypes";
import { sheetGeometryStylePass } from "doc/plans/primitives/primitiveStyles";
import type { TransformWithComponents } from "doc/plans/sheetGeometryTools";
import { sheetGeometryTransform } from "doc/plans/sheetGeometryTools";
import { isInteriorRoomEnclosure, isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import { getReportableUnitDesignatorLabelIds } from "doc/utils/labelTools";
import { reportingUnitLabelFromEntityId } from "doc/utils/reportingUnitDocTools";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { aabb2AddPoint, aabb2Center, aabb2Copy, aabb2Scale } from "geom/aabb2";
import { aabb2New } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import { aabb3Union } from "geom/aabb3";
import { aabb3New } from "geom/aabb3Types";
import { faceAABB3 } from "geom/brep";
import { isPointInsideBSP } from "geom/bsp";
import { DIST_EPSILON, DIST_EPSILON_SQUARED, ONE_MINUS_DIST_EPSILON } from "geom/core";
import { line2Normal } from "geom/line2";
import type { Mat3 } from "geom/mat3";
import type { Mat4 } from "geom/mat4";
import { mat4Dot, mat4Ident, mat4LookAt, mat4Mult } from "geom/mat4";
import { offsetPolyLine2 } from "geom/offset";
import { planeNew, planeNewXY, planeNormal, planeProject, planeProjectPolyLine3 } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polygonUnion } from "geom/polygon";
import {
  polyLine2AABB2,
  polyLine2ByTwoPoints,
  polyLine2Close,
  polyLine2EdgeEval,
  polyLine2EdgeNormal,
  polyLine2EdgeTangent,
  polyLine2End,
  polyLine2Extend,
  polyLine2FromAABB2,
  polyLine2MergeCoincidentVertices,
  polyLine2Move,
  polyLine2SetCCW,
  polyLine2Start,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3ByTwoPoints,
  polyLine3Copy,
  polyLine3EdgeTangent,
  polyLine3Move,
  polyLine3OnPlane,
  polyLine3Reverse,
  polyLine3StartPoint,
} from "geom/polyLine3";
import { ray2Eval } from "geom/ray2";
import { vec2Dot, vec2Perp, vec2Rotate, vec2Scale } from "geom/vec2";
import type { Vec3 } from "geom/vec3";
import { vec3CrossNormalize, vec3Dot, vec3Negate } from "geom/vec3";
import { getElevationScaleFactor } from "lib/components/studio/elevationAnnotationUtils";
import { generateEdgeDimTag } from "lib/house/annotation/tags/dimTag";
import { type ElevMarker, generateElevMarkersTagSheetGeometries } from "lib/house/annotation/tags/elevMarkersTag";
import { generateElevNoteSheetGeometries } from "lib/house/annotation/tags/elevNote";
import { generateElevSlopeTagSheetGeometry } from "lib/house/annotation/tags/elevSlopeTag";
import { elevationNoteMirror } from "lib/house/elements/viewNotes/elevationNote";
import type { ManualEntityAnnotationInfo } from "lib/house/pdf/annotateEntitiesInView";
import {
  placeEntityAnnotationsOnBoxPerimeter,
  collectEntityAnnotationsInView,
  legacyPlaceEntityAnnotationsOnPerimeter,
  placeEntityAnnotationsInPlace,
  getBoundingBoxLabelSpacing,
  serializeBoundingBoxPlacementRepro,
} from "lib/house/pdf/annotateEntitiesInView";
import { CollisionSystem2, elevationSheetGeometryAABB2, sheetGeometryCollisionAABBs } from "lib/house/pdf/collision2";
import { getRenderSizeWithTitleBlock } from "lib/house/pdf/getRenderSizeWithTitleBlock";
import {
  elevationAnnotationPlacementErrorDetail,
  isElevationAnnotationPlacementError,
} from "lib/house/pdf/PDFUserFacingError";
import { orthographicDrawingBB } from "lib/house/pdf/renderBuildingPerspectiveDrawing";
import {
  RoofEdgeDimType,
  extractUniqueRoofPlaneTags,
  groupRoofEdgeLines,
  joinRoofEdgesWithOverlappingIntervals,
  makeRoofHeightElevAnnos,
} from "lib/house/plans/elevationDrawing";
import { getBuildingFoundationZ } from "lib/house/plans/perspective";
import { DEFAULT_LARGE_FONT_SIZE } from "pdf/planFonts";
import { SVGElementToCollisionWeight } from "svg/SVGElementToCollisionWeight";
import type { WASMState } from "./assets/loadWasm";
import type { ElevationDrawing, RoofEdgeLine } from "./elevationDrawing";
import { generateGradeLineSheetGeometry } from "./elevationGradeLine";
import { computeModeledGradeLinePolyLines } from "./elevationGradeLineModeled";
import { renderHLREntityPolygonsWasm } from "./hlr/renderHLREntityPolygonsWasm";
import type { EntityPolyLine } from "./perspectiveEntities";
import { renderPerspectiveEntityPolygons } from "./perspectiveEntities";
import type { RoofAnnotationSettings } from "./planSettingsDefault";
import { evalRoofAnnotationSetting, getRoofAnnotationSettings } from "./planSettingsDefault";

export function elevationViewPlaneFromEyePos(eyePos: Vec3, target: Vec3 = [0, 0, 0]) {
  if (!(eyePos[0] !== target[0] || eyePos[1] !== target[1])) {
    console.assert(false);
  }
  const [planeUX, planeUY, planeUZ] = vec3CrossNormalize(
    0,
    0,
    1,
    eyePos[0] - target[0],
    eyePos[1] - target[1],
    eyePos[2] - target[2],
  );
  const viewPlane = planeNew(0, 0, 0, planeUX, planeUY, planeUZ, 0, 0, 1);
  return viewPlane;
}

function addPlanGeometryToCollisionSystem(
  collisionSystem: CollisionSystem2,
  geometry: TaggedPlanGeometry,
  weightMultiplier = 1,
  textScalingFactor = 1,
  categoriesToExcludeFromCollisions?: readonly SheetGeometryCategory[],
  // Generated annotations reserve label space but must not make target geometry unreachable by leader lines.
  blocksLeaderLines = false,
): AABB2 | null {
  if (categoriesToExcludeFromCollisions?.includes(geometry.category)) {
    return null;
  }

  const bbox: AABB2 | null = elevationSheetGeometryAABB2(geometry, textScalingFactor);
  if (!bbox) {
    return null;
  }

  let weight = 0;
  switch (geometry.type) {
    case SheetGeometryType.CIRCLE:
    case SheetGeometryType.ARC: {
      weight = SVGElementToCollisionWeight["circle"];
      break;
    }
    case SheetGeometryType.POLYLINE: {
      weight = SVGElementToCollisionWeight["default"];
      break;
    }
    case SheetGeometryType.TEXT: {
      weight = SVGElementToCollisionWeight["text"];
      break;
    }
    default:
      return bbox;
  }

  collisionSystem.insert(bbox, weight * weightMultiplier, blocksLeaderLines);
  return bbox;
}

class ElevationAnnotationGeometryBuilder implements PlanGeometryBuilderWithCollider {
  private builder: PlanGeometryBuilder;
  public readonly bb: AABB2;
  public readonly dwgCenterX: number;
  public readonly dwgCenterY: number;
  public readonly paperSpaceToModelSpaceTransform: Mat3;
  public readonly modelSpaceToPaperSpaceTransform: Mat3;
  public readonly pageWidth: SheetInches;
  public readonly pageHeight: SheetInches;
  public readonly worldBB: AABB2;

  constructor(
    // Note: the collision system is in paper space. Switching to world space will require changing the algorithm.
    private collisionSystem: CollisionSystem2,
    private textScalingFactor = 1,
    worldAABB: AABB2,
    public readonly scaleFactor: number,
    private readonly categoriesToExcludeFromCollisions?: readonly SheetGeometryCategory[],
    public readonly viewportScale?: number,
  ) {
    this.worldBB = worldAABB;
    this.builder = new PlanGeometryBuilder();

    this.dwgCenterX = (worldAABB.minX + worldAABB.maxX) / 2;
    this.dwgCenterY = (worldAABB.minY + worldAABB.maxY) / 2;

    const renderDims = getRenderSizeWithTitleBlock();
    this.pageWidth = renderDims.renderWidth;
    this.pageHeight = numDiv(renderDims.renderHeight, 2);

    this.bb = aabb2Scale(aabb2Copy(worldAABB), this.scaleFactor);

    // Spelled out rather than spread from `this`: only these fields are part of DrawingLayout,
    // and spreading a class instance drops its prototype.
    const fakeLayout: DrawingLayout = {
      drawingLayoutType: DRAWING_LAYOUT,
      type: undefined,
      pageWidth: this.pageWidth,
      pageHeight: this.pageHeight,
      dwgCenterX: this.dwgCenterX,
      dwgCenterY: this.dwgCenterY,
      pageCenterX: this.pageWidth / 2,
      pageCenterY: this.pageHeight / 2,
      scaleFactor: this.scaleFactor,
      bb: this.bb,
    };

    this.paperSpaceToModelSpaceTransform = paperSpaceToModelSpaceTransform(fakeLayout, false);
    this.modelSpaceToPaperSpaceTransform = modelSpaceToPaperSpaceTransform(fakeLayout);

    assert(isFinite(this.scaleFactor) && this.scaleFactor > 0);
  }

  toWorldSpaceGeometry(paperSpaceGeometry: SheetGeometry): SheetGeometry {
    // TODO: remove transformation from here
    const transformWithComponents: TransformWithComponents = {
      transform: this.paperSpaceToModelSpaceTransform,
      transformScale: [1 / this.scaleFactor, 1 / this.scaleFactor],
      transformRotation: 0,
    };
    return sheetGeometryTransform(paperSpaceGeometry, transformWithComponents);
  }

  toPaperSpaceGeometry(worldSpaceGeometry: SheetGeometry): SheetGeometry {
    // TODO: remove transformation from here
    const transformWithComponents: TransformWithComponents = {
      transform: this.modelSpaceToPaperSpaceTransform,
      transformScale: [this.scaleFactor, this.scaleFactor],
      transformRotation: 0,
    };
    return sheetGeometryTransform(worldSpaceGeometry, transformWithComponents);
  }

  addPaperSpaceDebugGeometry(paperSpaceGeometry: SheetGeometry, category: SheetGeometryCategory) {
    const worldSpaceGeometry = this.toWorldSpaceGeometry(paperSpaceGeometry);
    this.builder.addGeometry(worldSpaceGeometry, category);
  }

  addPaperSpaceGeometry(
    paperSpaceGeometry: SheetGeometry,
    category: SheetGeometryCategory,
    weightMultiplier?: number,
    blocksLeaderLines = false,
  ) {
    const categorizedGeometry = supplyGeometryTagIfMissing(paperSpaceGeometry, category);
    addPlanGeometryToCollisionSystem(
      this.collisionSystem,
      categorizedGeometry,
      weightMultiplier,
      this.textScalingFactor,
      this.categoriesToExcludeFromCollisions,
      blocksLeaderLines,
    );

    const worldSpaceGeometry = this.toWorldSpaceGeometry(paperSpaceGeometry);
    this.builder.addGeometry(worldSpaceGeometry, category);
  }

  addPaperSpacePolylineGeometryWithCollision(
    paperSpaceGeometry: SheetPolylineGeometry,
    category: SheetGeometryCategory,
    weightMultiplier = 1,
  ): void {
    const weight = SVGElementToCollisionWeight["default"] * weightMultiplier;
    if (!this.categoriesToExcludeFromCollisions?.includes(category)) {
      for (const path of paperSpaceGeometry.paths) {
        this.collisionSystem.insertPolyLine2(path, weight, true);
      }
    }

    const worldSpaceGeometry = this.toWorldSpaceGeometry(paperSpaceGeometry);
    this.builder.addGeometry(worldSpaceGeometry, category);
  }

  addWorldSpaceGeometry(worldSpaceGeometry: SheetGeometry, category: SheetGeometryCategory) {
    this.builder.addGeometry(worldSpaceGeometry, category);
  }

  addWorldSpaceGeometryWithCollision(
    worldSpaceGeometry: SheetGeometry,
    category: SheetGeometryCategory,
    weightMultiplier?: number,
  ) {
    this.addWorldSpaceGeometry(worldSpaceGeometry, category);

    const paperSpaceGeometry = this.toPaperSpaceGeometry(worldSpaceGeometry);
    const categorizedGeometry = supplyGeometryTagIfMissing(paperSpaceGeometry, category);

    addPlanGeometryToCollisionSystem(
      this.collisionSystem,
      categorizedGeometry,
      weightMultiplier,
      this.textScalingFactor,
      this.categoriesToExcludeFromCollisions,
    );
  }

  xToPaperSpace(x: number): SheetInches {
    x -= this.dwgCenterX;
    x *= this.scaleFactor;
    x += this.pageWidth / 2;
    return sheetInchesNew(x);
  }

  yToPaperSpace(y: number): SheetInches {
    y -= this.dwgCenterY;
    y *= this.scaleFactor;
    y += this.pageHeight / 2;
    y *= -1;
    y += this.pageHeight;
    return sheetInchesNew(y);
  }

  ptToPaperSpace(x: number, y: number): [SheetInches, SheetInches] {
    return [this.xToPaperSpace(x), this.yToPaperSpace(y)];
  }

  get taggedGeometries() {
    return this.builder.taggedGeometries;
  }
}

/** Callback for reporting errors during elevation annotation generation. */
export type ElevationGeneratorErrorCallback = (error: Error) => void;

type ElevationGenerator = (
  options: GenerateElevationAnnotationOptions,
  onError?: ElevationGeneratorErrorCallback,
) => void | Promise<void>;

function addAutoNotePlacementFailureGeometry(primitivesOut: ElevationAnnotationGeometryBuilder): void {
  const [centerX, centerY] = aabb2Center(primitivesOut.worldBB);
  primitivesOut.addWorldSpaceGeometry(
    sheetTextGeometryNew({
      text: "Error: Auto-note placement failed",
      x: centerX,
      y: centerY,
      fontColor: colors.ruby,
      fontSize: DEFAULT_LARGE_FONT_SIZE,
      background: TextBackground.SOLID,
      backgroundColor: colors.white,
      alignment: TextAlignment.CENTER,
      verticalAlignment: TextVerticalAlignment.CENTER,
      placementXAnchor: PlacementXAnchor.CENTER,
      placementYAnchor: PlacementYAnchor.CENTER,
      rotationBasis: "SHEET",
    }),
    SheetGeometryCategory.MATERIAL_TAGS,
  );
}

export async function generateSheetGeometryElevationAutoAnnotations(
  drawing: ElevationDrawing,
  generator: ElevationGenerator,
): Promise<PlanGeometryGeneratorResult> {
  const { building } = drawing;

  const collisionSystem = new CollisionSystem2();

  const scaleFactor = await getElevationScaleFactor({
    building,
    eyePos: drawing.eyePos,
  }); // TODO: Prop selectors?
  const viewMatrix = mat4LookAt(drawing.eyePos, [0, 0, 0], [0, 0, 1]);
  const worldAABB = await orthographicDrawingBB(building, viewMatrix, true, drawing.visibilitySelectors);
  const primitivesOut = new ElevationAnnotationGeometryBuilder(
    collisionSystem,
    drawing.textScalingFactor,
    worldAABB,
    scaleFactor,
    drawing.categoriesToExcludeFromCollisions,
    drawing.viewportScale,
  );

  const levels = getElevationLevels(building);

  const reportedErrors: Error[] = [];
  await generator({ drawing, levels, primitivesOut, collisionSystem }, (error) => reportedErrors.push(error));

  const placementErrors = reportedErrors.filter(isElevationAnnotationPlacementError);
  const errors = reportedErrors.filter((error) => !isElevationAnnotationPlacementError(error));
  let diagnostics: PlanGeometryGenerationDiagnostic[] | undefined;
  if (placementErrors.length > 0) {
    addAutoNotePlacementFailureGeometry(primitivesOut);
    diagnostics = [
      {
        code: "ELEVATION_AUTO_NOTE_PLACEMENT_FAILED",
        severity: "ERROR",
        category: SheetGeometryCategory.MATERIAL_TAGS,
        message: placementErrors.map((error) => elevationAnnotationPlacementErrorDetail(error.message)).join("\n"),
        ...(drawing.viewportOrViewName ? { viewportName: drawing.viewportOrViewName } : {}),
        ...(drawing.sheetName ? { sheetName: drawing.sheetName } : {}),
      },
    ];
  }

  const taggedGeometries = primitivesOut.taggedGeometries;

  return { taggedGeometries, errors: errors.length > 0 ? errors : undefined, diagnostics };
}

interface GenerateElevationAnnotationOptions {
  drawing: ElevationDrawing;
  levels: Level[];
  primitivesOut: ElevationAnnotationGeometryBuilder;
  collisionSystem: CollisionSystem2;
}

export function generateFastElevationAnnotations({
  drawing,
  levels,
  primitivesOut,
}: GenerateElevationAnnotationOptions): void {
  const {
    building,
    eyePos,
    includeGradeLine,
    gradeLineSource,
    textScalingFactor = 1,
    dimensionPrecision = DimensionPrecision.PRECISION_1_8,
    noSettings = true,
    elevationAnnotationParams: elevationAnnotationParams,
    selectedReportingUnitLabelId,
  } = drawing;

  // Elevation auto-annotations (dimensions, top-of-plate/elevation markers, grade line, roof dims,
  // window tags, …) are building-wide and not meaningfully attributable to a single reporting unit,
  // so they are hidden entirely while the viewport is filtered to a unit rather than regenerated.
  if (selectedReportingUnitLabelId) {
    return;
  }

  const bbMinX = sheetInchesNew(primitivesOut.bb.minX);
  const bbMaxX = sheetInchesNew(primitivesOut.bb.maxX);

  // determine view bounds and orientation
  const xLimit = numSub(primitivesOut.pageWidth, sheetPointsToInches(sheetPointsNew(225)));
  const cX = primitivesOut.xToPaperSpace(0);

  const {
    roofSystems: roofs,
    rooms,
    roomsByLevel,
    windowPlacements: windows,
    levelsAttributes,
    settings: { planset: builtInPlanSettings },
  } = building;

  const viewPlane = elevationViewPlaneFromEyePos(eyePos);

  const levelRoofFaceInfos = getRoofsTopFacesByLevel(roofs, building.levels);
  const elevMarkerAnnos: ElevMarker[] = [];
  const levelRoofFaces = convertRoofFaceInfoToFaceListArray(levelRoofFaceInfos);

  const dormerLevelRoofFaces = convertRoofFaceInfoToFaceListArray(levelRoofFaceInfos, ONLY_DORMER_FACE_TYPE);

  if (noSettings || building.settings.planset.automatedDatumAnnotations) {
    makeRoofHeightElevAnnos(levelRoofFaces, elevMarkerAnnos, viewPlane, primitivesOut, dimensionPrecision);
  }
  const annoX = numMin(numSub(xLimit, cX), bbMaxX);

  const levelOffset = getLevelOffset(
    primitivesOut,
    elevationAnnotationParams,
    building.settings.planset.elevationNotePlacementType,
    textScalingFactor,
  );

  for (const level of levels) {
    const { floorElevation, topOfWall, id: id } = level;
    const { windowHeadHeight: levelWindowHeadHeight } = levelsAttributes[id];
    const levelRooms = roomsOnLevel(id, rooms, roomsByLevel);

    const { floorOffsets } = getUniqueFloorAndCeilingOffsets(levelRooms, building, viewPlane);

    const baseFloorElevation = floorElevation + floorOffsets[0];
    const cY = primitivesOut.yToPaperSpace(baseFloorElevation);

    const topOfWallHeightY = primitivesOut.yToPaperSpace(floorElevation + topOfWall);

    if (noSettings || building.settings.planset.showWindowHeaderDimsOnElevations) {
      const frontFacingWindows = getFrontFacingWindows(viewPlane, id, windows);
      if (frontFacingWindows.length > 0) {
        const mergedWindowSettings = getMergedWindowSettings({
          window: frontFacingWindows[0],
          descriptor: frontFacingWindows[0].descriptor,
          homeSettings: building.settings,
          entitySettings: building.doc.entitySettings,
          activeSettingsSets: building.settingsSets,
          optionChoices: building.optionChoices,
          settings: building.doc.settings,
        });

        const windowHeadHeight = mergedWindowSettings?.windowHeadHeight ?? levelWindowHeadHeight;
        const windowHeadElevation = windowHeadHeight + baseFloorElevation;

        const dimtags = generateEdgeDimTag({
          x: cX,
          x1: numSub(annoX, sheetPointsToInches(sheetPointsNew(25))),
          x2: numSub(annoX, sheetPointsToInches(sheetPointsNew(25))),
          y1: primitivesOut.yToPaperSpace(windowHeadElevation),
          y2: cY,
          dimVal: windowHeadHeight,
          dimensionPrecision,
          suffix: " WIN HEAD",
          offset: numAdd(sheetInchesNew(0.5), levelOffset),
        });
        for (const dimtag of dimtags) {
          primitivesOut.addPaperSpaceGeometry(dimtag, SheetGeometryCategory.WINDOW_HEADER_ANNOTATION);
        }
      }
    }

    if (noSettings || building.settings.planset.automatedDatumAnnotations) {
      for (const floorOffset of floorOffsets) {
        const currFloorElevation = floorElevation + floorOffset;
        // create floor elevation annotation
        const elevMarker: ElevMarker = {
          levelName: `T.O. ${levelName(level, true)}`,
          elevationText: `${formatWithPrecision(currFloorElevation, dimensionPrecision, false, true)}`,
          zLevel: primitivesOut.yToPaperSpace(currFloorElevation),
          priority: 1,
        };
        elevMarkerAnnos.push(elevMarker);
      }

      elevMarkerAnnos.push({
        levelName: `T.O. PLATE`,
        elevationText: `${formatWithPrecision(topOfWall, dimensionPrecision, false, true)}`,
        zLevel: primitivesOut.yToPaperSpace(floorElevation + topOfWall),
        priority: 1,
        category: SheetGeometryCategory.TOP_OF_PLATE_MARKERS,
      });
      const dimtags = generateEdgeDimTag({
        x: cX,
        x1: annoX,
        x2: annoX,
        y1: topOfWallHeightY,
        y2: cY,
        dimVal: topOfWall,
        offset: numAdd(sheetInchesNew(0.2), numAdd(sheetInchesNew(0.25 * textScalingFactor), levelOffset)),
        dimensionPrecision,
      });
      for (const dimtag of dimtags) {
        primitivesOut.addPaperSpaceGeometry(dimtag, SheetGeometryCategory.TOP_OF_PLATE_DIMENSIONS);
      }
    }
  }

  // create floor elevation annotation
  const elevMarkersTag = generateElevMarkersTagSheetGeometries({
    elevMarkers: elevMarkerAnnos,
    x: numAdd(cX, levelOffset),
    minX: numSub(bbMinX, levelOffset),
    maxX: numMin(numSub(xLimit, cX), bbMaxX),
    offsetLimit: elevationAnnotationParams?.levelAnnotationVerticalSpacing,
  });
  elevMarkersTag.forEach((geom) => primitivesOut.addPaperSpaceGeometry(geom, SheetGeometryCategory.ELEVATION_MARKERS));

  // create top of plate elevation annotation
  const topOfPlateMarkersTag = generateElevMarkersTagSheetGeometries({
    elevMarkers: elevMarkerAnnos,
    x: numAdd(cX, levelOffset),
    minX: numSub(bbMinX, levelOffset),
    maxX: numMin(numSub(xLimit, cX), bbMaxX),
    category: SheetGeometryCategory.TOP_OF_PLATE_MARKERS,
    offsetLimit: elevationAnnotationParams?.levelAnnotationVerticalSpacing,
  });
  topOfPlateMarkersTag.forEach((geom) =>
    primitivesOut.addPaperSpaceGeometry(geom, SheetGeometryCategory.TOP_OF_PLATE_MARKERS),
  );

  const groundZ = buildingGroundZ(building);
  if (includeGradeLine) {
    let foundationZ = getBuildingFoundationZ(building);
    const bottomLev = bottomLevel(building.levels);
    if (!bottomLev) {
      return;
    }

    if (bottomLev.floorIndex < 0) {
      foundationZ = groundZ;
    }

    // Paper-space polylines, absolute (already offset by cX), one per continuous run of grade.
    const gradeLinePolyLines: PolyLine2[] = [];

    const modeledPolyLines =
      gradeLineSource === GradeLineSource.MODELED ? computeModeledGradeLinePolyLines(building, viewPlane) : undefined;

    for (const modelSpacePolyLine of modeledPolyLines ?? []) {
      const paperSpacePoints: number[] = [];
      for (let i = 0; i < modelSpacePolyLine.length; i += 2) {
        paperSpacePoints.push(
          primitivesOut.xToPaperSpace(modelSpacePolyLine[i]),
          primitivesOut.yToPaperSpace(modelSpacePolyLine[i + 1]),
        );
      }

      gradeLinePolyLines.push(polyLine2New(paperSpacePoints));
    }

    // The flat line: the whole grade line for the Default source, since `modeledPolyLines` is undefined
    // there and the loop above adds nothing, and the fallback when Modeled produced nothing usable.
    if (gradeLinePolyLines.length === 0) {
      const cGradeZ = primitivesOut.yToPaperSpace(groundZ);
      const cGradePolyLine = polyLine2New([bbMinX, cGradeZ, bbMaxX, cGradeZ]);

      if (Math.abs(foundationZ - groundZ) < DIST_EPSILON) {
        polyLine2Extend(cGradePolyLine, ONE_INCH, ONE_INCH);
      }

      gradeLinePolyLines.push(polyLine2Move(cGradePolyLine, cX, ZERO_INCHES));
    }

    for (const polyLine of gradeLinePolyLines) {
      // Deliberately unstyled: the GRADE_LINE category stylesheet owns width/color/dash so View
      // Template overrides apply. Geometry-level style would win over them.
      primitivesOut.addPaperSpaceGeometry(
        generateGradeLineSheetGeometry({ polyLine }),
        SheetGeometryCategory.GRADE_LINE,
      );
    }
  }

  // since we don't fill in defaults for the annotation settings when generating roofs, we need to resolve the settings here
  // to either the built in plan set settings (built in plans) or just the system default (live plans)
  const resolvedPlanSettings = getRoofAnnotationSettings(builtInPlanSettings, noSettings);

  const roofAnnotationOptions = {
    roofAnnotationSettings: resolvedPlanSettings,
    dimensionPrecision,
    primitivesOut,
    viewPlane,
    building,
  };

  dimensionGableEndWindows(roofAnnotationOptions);
  dimensionDormerWindows(roofAnnotationOptions);
  tagGableEndWindows(roofAnnotationOptions);
  tagDormerWindows(roofAnnotationOptions);
  dimensionRoofs(roofAnnotationOptions);
  // label visible roof slopes and roof edges using extruded mass occlusion
  labelAndDimensionRoofSlopes(roofAnnotationOptions, levelRoofFaces, dormerLevelRoofFaces);
}

/**
 * Add manual elevation annotations (notes, linework, details, dimensions) to the collision system
 * so that auto-generated annotations avoid overlapping with user-placed annotations.
 */
function addManualElevationAnnotationsToCollisionSystem({
  drawing,
  primitivesOut,
  collisionSystem,
}: {
  drawing: ElevationDrawing;
  primitivesOut: ElevationAnnotationGeometryBuilder;
  collisionSystem: CollisionSystem2;
}): void {
  const {
    building,
    referenceFrame,
    sheetGeometryStyler,
    elevationDebugOptions,
    globalParameterValues,
    textScalingFactor = 1,
  } = drawing;

  // Generate sheet geometry from manual elevation annotations
  const manualAnnotationGeometry = makeSheetGeometryFromElevationViewAnnotations({
    building,
    referenceFrame,
    dimensionPrecision: drawing.dimensionPrecision,
    globalParameterValues,
  });

  // Retain note leaders as precise polylines; other manual annotation geometry uses bounding boxes.
  for (const category of objectTypedKeys(manualAnnotationGeometry.taggedGeometries)) {
    const geometries = manualAnnotationGeometry.taggedGeometries[category];
    if (!geometries) {
      continue;
    }

    // Apply view-specific styling using the passed styler for accurate bounding box calculation
    const styledGeometries = sheetGeometryStyler ? sheetGeometryStyler(geometries, category) : geometries;

    for (const geom of styledGeometries) {
      // The collision system operates in paper space.
      const paperSpaceGeom = primitivesOut.toPaperSpaceGeometry(geom);
      const scaledPaperSpaceGeom =
        paperSpaceGeom.type === SheetGeometryType.TEXT
          ? sheetGeometryStylePass(paperSpaceGeom, undefined, textScalingFactor)
          : paperSpaceGeom;

      if (
        scaledPaperSpaceGeom.type === SheetGeometryType.POLYLINE &&
        scaledPaperSpaceGeom.anchorPoint !== undefined &&
        !scaledPaperSpaceGeom.fill
      ) {
        for (const path of scaledPaperSpaceGeom.paths) {
          collisionSystem.insertPolyLine2(path, 1, true);

          if (elevationDebugOptions.showElevationNoteEntities === ElevationNoteEntityDebugMode.TAGGED_PLUS_BOX) {
            primitivesOut.addPaperSpaceDebugGeometry(
              sheetPolylineGeometryNew({
                paths: [path],
                strokeColor: "orange",
                strokeOpacity: percentNew(1),
                linewidth: sheetPointsNew(0.5),
              }),
              SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES,
            );
          }
        }
        continue;
      }

      const aabbs = sheetGeometryCollisionAABBs(scaledPaperSpaceGeom);
      for (const aabb of aabbs) {
        // Weight doesn't matter because the bbox method blocks all overlapping geometries.
        collisionSystem.insert(aabb, 1, true);

        if (elevationDebugOptions.showElevationNoteEntities === ElevationNoteEntityDebugMode.TAGGED_PLUS_BOX) {
          const pl = polyLine2FromAABB2(aabb);
          primitivesOut.addPaperSpaceDebugGeometry(
            sheetPolylineGeometryNew({
              paths: [pl],
              fill: true,
              fillOpacity: percentNew(0.5),
              fillColor: "orange",
              strokeOpacity: percentNew(1),
              linewidth: sheetPointsNew(0.5),
            }),
            SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES,
          );
        }
      }
    }
  }
}

export async function generateSlowElevationAnnotations(
  { drawing, primitivesOut, collisionSystem, levels }: GenerateElevationAnnotationOptions,
  onError?: ElevationGeneratorErrorCallback,
): Promise<void> {
  const ENABLE_BOUNDING_BOX_REPRO_LOGGING = false;

  console.time("generateSlowElevationAnnotations");

  const scaleFactor = await getElevationScaleFactor({
    building: drawing.building,
    eyePos: drawing.eyePos,
  }); // TODO: Prop selectors?
  const viewMatrix = mat4LookAt(drawing.eyePos, [0, 0, 0], [0, 0, 1]);
  const worldAABB = await orthographicDrawingBB(drawing.building, viewMatrix, true, drawing.visibilitySelectors);

  const fastPrimitivesOut = new ElevationAnnotationGeometryBuilder(
    collisionSystem,
    drawing.textScalingFactor,
    worldAABB,
    scaleFactor,
    drawing.categoriesToExcludeFromCollisions,
  );

  // Add the fast primitives to the collision system, discarding the geometries. Note that this duplicates work
  // with the main thread. The long term plan is to remove the slow generators entirely, which would remove the
  // need for the duplicate work.
  generateFastElevationAnnotations({ drawing, levels, primitivesOut: fastPrimitivesOut, collisionSystem });

  const {
    building,
    wasmBuildingBSP: buildingBSP,
    jsBuildingBSP,
    hlrFaceGroups,
    hlrLazyPropMeshes,
    entityTable,
    eyePos,
    allowBelowGroundAnnotations,
    elevationDebugOptions,
    // DO NOT USE textScalingFactor to actually scale text geometries. Only for purposes of offsetting other geometries
    // by a neighboring text geometry's size, or taking the AABB2 of a text geometry.
    textScalingFactor = 1,
    wasm,
    elevationAnnotationParams,
    sheetGeometryStyler,
  } = drawing;

  const { debugElevationColliders, showElevationNoteEntities } = elevationDebugOptions;

  const projectionMatrix = mat4Ident();

  let entityPolylines: EntityPolyLine[];
  let hlrDebugRegions: PolyLine2[] | undefined;
  let hlrDegenerateRegions: PolyLine2[] | undefined;

  if (hlrFaceGroups) {
    console.time("renderHLREntityPolygonsWasm");
    const hlrResult = await renderHLREntityPolygonsWasm({
      faceGroups: hlrFaceGroups,
      lazyPropMeshes: hlrLazyPropMeshes ?? [],
      viewMatrix,
      wasm,
      onError,
    });
    entityPolylines = hlrResult.entityPolylines;
    hlrDebugRegions = hlrResult.debugRegions;
    hlrDegenerateRegions = hlrResult.degenerateRegions;
    console.timeEnd("renderHLREntityPolygonsWasm");
  } else if (jsBuildingBSP) {
    console.time("renderPerspectiveEntityPolygons (js)");
    entityPolylines = renderPerspectiveEntityPolygons({
      sceneBSP: ensureDefined(jsBuildingBSP),
      viewMatrix,
      isPerspectiveMatrix: false,
      prjMatrix: projectionMatrix,
      entityTable,
    });
    console.timeEnd("renderPerspectiveEntityPolygons (js)");
  } else {
    entityPolylines = renderRustPerspectiveEntityPolygons(buildingBSP, wasm, viewMatrix, eyePos);
  }

  const { settings, mirror } = building;

  const viewPlane = elevationViewPlaneFromEyePos(eyePos);

  const [viewX, viewY] = planeNormal(viewPlane);

  const elevationNotePlacementType = building.settings.planset.elevationNotePlacementType;

  assertIsNotUndefined(entityTable);

  const finalMatrix = mat4Mult(projectionMatrix, viewMatrix);
  let notes = getElevationManualNotes({ building, viewX, viewY, finalMatrix, mirror });

  // Auto entity annotations carry no unit attribution, so hide them when filtered to a unit.
  const autoNotes = drawing.selectedReportingUnitLabelId
    ? []
    : collectEntityAnnotationsInView({
        collisionSystem,
        entityTable,
        primitivesOut,
        settings: settings.viewNote,
        entityPolylines,
        elevationNotePlacementType,
        polylineUnionLimit: 1,
        showElevationNoteEntities,
      });

  if (drawing.selectedReportingUnitLabelId) {
    const reportingUnitLabelIds = getReportableUnitDesignatorLabelIds(building.doc);
    notes = notes.filter((note) => {
      const room = building.rooms[note.elevationNote.roomId];
      const handle = roomEntityHandleForSelectingReportingUnit(room);
      return (
        reportingUnitLabelFromEntityId(building.doc, reportingUnitLabelIds, handle) ===
        drawing.selectedReportingUnitLabelId
      );
    });
  }

  switch (elevationNotePlacementType) {
    case ElevationNotePlacementType.IN_PLACE: {
      addManualElevationNotesInPlace({ notes, primitivesOut });
      const groundZ = buildingGroundZ(building);
      placeEntityAnnotationsInPlace(
        autoNotes,
        collisionSystem,
        SheetGeometryCategory.MATERIAL_TAGS,
        primitivesOut,
        allowBelowGroundAnnotations,
        groundZ,
        textScalingFactor,
      );
      break;
    }
    case ElevationNotePlacementType.ON_PERIMETER: {
      legacyPlaceEntityAnnotationsOnPerimeter(
        [...autoNotes, ...notes],
        SheetGeometryCategory.MATERIAL_TAGS,
        primitivesOut,
        textScalingFactor,
        sheetGeometryStyler,
      );
      break;
    }
    case ElevationNotePlacementType.BOUNDING_BOX: {
      // Add manual elevation annotations to the collision system so auto-annotations avoid them
      addManualElevationAnnotationsToCollisionSystem({
        drawing,
        primitivesOut,
        collisionSystem,
      });

      addManualElevationNotesInPlace({ notes, primitivesOut });

      let didCaptureBoundingBoxRepro = false;
      const captureBoundingBoxRepro = (errorMessage: string): void => {
        if (didCaptureBoundingBoxRepro || !ENABLE_BOUNDING_BOX_REPRO_LOGGING) {
          return;
        }

        didCaptureBoundingBoxRepro = true;
        const repro = serializeBoundingBoxPlacementRepro({
          autoNotes,
          collisionSystem,
          primitivesOut,
          showElevationNoteEntities,
          elevationAnnotationParams,
          sheetGeometryStyler,
          textScalingFactor,
          errorMessage,
        });

        console.error("Bounding box placement repro JSON:\n" + JSON.stringify(repro, undefined, 2));
      };

      try {
        await placeEntityAnnotationsOnBoxPerimeter({
          autoNotes,
          collisionSystem,
          primitivesOut,
          showElevationNoteEntities,
          elevationAnnotationParams,
          sheetGeometryStyler,
          textScalingFactor,
          onError: (error) => {
            captureBoundingBoxRepro(error.message);
            onError?.(error);
          },
        });
      } catch (error) {
        captureBoundingBoxRepro(error instanceof Error ? error.message : String(error));
        throw error;
      }
      break;
    }
  }

  if (debugElevationColliders) {
    renderColliderVisualization(collisionSystem, primitivesOut);
  }

  if (debugElevationColliders && hlrDebugRegions) {
    renderHLRRegionVisualization(hlrDebugRegions, primitivesOut, HLR_REGION_DEBUG_STYLE);
  }

  if (debugElevationColliders && hlrDegenerateRegions) {
    renderHLRRegionVisualization(hlrDegenerateRegions, primitivesOut, HLR_DEGENERATE_REGION_DEBUG_STYLE);
  }

  console.timeEnd("generateSlowElevationAnnotations");
}

export function renderRustPerspectiveEntityPolygons(
  buildingBSP: number | undefined,
  wasm: WASMState,
  viewMatrix: Mat4,
  eyePos: Vec3,
) {
  console.time("wasm.renderBSPToPolygons");

  const wasmPolygons = buildingBSP
    ? wasm.wasm?.renderBSPToPolygons(
        ensureDefined(buildingBSP),
        viewMatrix,
        false,
        eyePos[0],
        eyePos[1],
        eyePos[2],
        0,
        0,
        0,
        [] /* clippingPlanes */,
      ) ?? []
    : [];

  const regroupedPolygons: { [key: string]: PolyLine2[] } = {};
  wasmPolygons.forEach((polygon) => {
    let polyline = polyLine2New(polygon.vertices.flatMap((v) => [v.x, v.y]));
    polyline = polyLine2MergeCoincidentVertices(polyline);
    if (polygon.vertices.length < 3) {
      return;
    }
    polyline = polyLine2Close(polyline);
    polyline = polyLine2SetCCW(polyline);
    polyline = offsetPolyLine2(polyline, DIST_EPSILON); // offsetting to help with unioning; will be undone after the unioning.
    const faceCategory = polygon.data;
    if (faceCategory !== undefined) {
      if (regroupedPolygons[faceCategory]) {
        regroupedPolygons[faceCategory].push(polyline);
      } else {
        regroupedPolygons[faceCategory] = [polyline];
      }
    }
  });

  const unionedPolygons: { [key: string]: PolyLine2[] } = {};
  for (const faceCategory in regroupedPolygons) {
    const polys = regroupedPolygons[faceCategory];
    if (polys.length === 0) {
      continue;
    }
    // TODO: the new polygon union is not working as expected. Need to debug why.
    //const unioned = polygonUnion([polys], undefined, PolygonRoundingMode.SNAP_ROUND).flatMap((p) => p);
    const unioned = polygonUnion(polys.map((p) => [p])).flatMap((p) => offsetPolyLine2(p[0], -DIST_EPSILON));
    if (unioned) {
      unionedPolygons[faceCategory] = unioned;
    }
  }

  const rustEntityPolylines: EntityPolyLine[] = Object.entries(unionedPolygons).map(([faceCategory, polys]) => {
    return { polylines: polys, entityId: faceCategory };
  });

  console.timeEnd("wasm.renderBSPToPolygons");
  return rustEntityPolylines;
}

function renderColliderVisualization(
  collisionSystem: CollisionSystem2,
  primitivesOut: ElevationAnnotationGeometryBuilder,
) {
  const allColliders = collisionSystem.all();
  for (const collider of allColliders) {
    const pl = collider.polyline ?? polyLine2FromAABB2(collider);

    primitivesOut.addPaperSpaceDebugGeometry(
      sheetPolylineGeometryNew({
        paths: [pl],
        linewidth: sheetPointsNew(collider.polyline ? 0.8 : 0.2),
        strokeColor: colors.sapphire,
        strokeOpacity: percentNew(1),
        fill: collider.polyline === undefined,
        fillColor: colors.sapphire,
        fillOpacity: percentNew(0.25 + collider.weight * 0.01),
      }),
      SheetGeometryCategory.DEBUG_ELEVATION_COLLIDERS,
    );
  }
}

const HLR_REGION_DEBUG_STYLE = { strokeColor: colors.emerald, fillColor: colors.emerald };
const HLR_DEGENERATE_REGION_DEBUG_STYLE = { strokeColor: colors.rubyDark, fillColor: colors.rubyDark };

function renderHLRRegionVisualization(
  regions: PolyLine2[],
  primitivesOut: ElevationAnnotationGeometryBuilder,
  { strokeColor, fillColor }: { strokeColor: string; fillColor: string },
) {
  for (const region of regions) {
    primitivesOut.addWorldSpaceGeometry(
      sheetPolylineGeometryNew({
        paths: [region],
        linewidth: sheetPointsNew(0.4),
        strokeColor,
        fill: true,
        fillColor,
        fillOpacity: percentNew(0.2),
      }),
      SheetGeometryCategory.DEBUG_ELEVATION_COLLIDERS,
    );
  }
}

function getFrontFacingWindows(
  viewPlane: Plane,
  levelId: string,
  windows: Table<GeneratedWindowPlacement>,
): GeneratedWindowPlacement[] {
  const frontFacingWindows: GeneratedWindowPlacement[] = [];

  const [viewNormalX, viewNormalY] = planeNormal(viewPlane);

  const isValidRoom = (room: Room | null | undefined): boolean => {
    return room !== null && room !== undefined && room.level.id === levelId;
  };

  const isPlaneFacingFront = (plane: Plane): boolean => {
    const [nX, nY] = planeNormal(plane);

    return vec2Dot(viewNormalX, viewNormalY, nX, nY) > DIST_EPSILON_SQUARED;
  };

  for (const windowId in windows) {
    const window = windows[windowId];
    const { frontRoom, backRoom, opening } = window;

    if (!opening || (!isValidRoom(frontRoom) && !isValidRoom(backRoom))) {
      continue;
    }

    const { frontPlane, backPlane } = opening;
    let exteriorFacePlane = backPlane;

    switch (window.type) {
      case "WINDOW": {
        if (window.wallStripDomain && !isWalledRoomEnclosure[ensureDefined(frontRoom).attributes.roomEnclosure]) {
          exteriorFacePlane = frontPlane;
        }
        break;
      }
      case "WINDOW_V2": {
        if (frontRoom && !isWalledRoomEnclosure[frontRoom.attributes.roomEnclosure]) {
          exteriorFacePlane = frontPlane;
        }
        break;
      }
    }

    if (isPlaneFacingFront(exteriorFacePlane)) {
      frontFacingWindows.push(window);
    }
  }

  return frontFacingWindows;
}

interface RoomOffsets {
  floorOffsets: number[];
  ceilingOffsets: number[];
}

export function getUniqueFloorAndCeilingOffsets(rooms: Room[], building: Building, viewPlane: Plane): RoomOffsets {
  const uniqueFloorOffsets: { [key: number]: boolean } = {};
  const uiniqueCeilingOffsets: { [key: number]: boolean } = {};

  const { roomFloorAssemblies } = building;

  const sortUniqueOffsets = (uniqueOffsets: { [key: number]: boolean }): number[] => {
    const offsets: number[] = [];
    for (const uniqueOffset in uniqueOffsets) {
      offsets.push(parseFloat(uniqueOffset));
    }

    if (offsets.length === 0) {
      offsets.push(0);
    }

    return offsets.sort();
  };

  for (const room of rooms) {
    if (isValidRoomForAddingOffsets(room, building, viewPlane)) {
      const floorElevationOffset = floorAssemblyBaseOffset(roomFloorAssemblies[room.id]);
      if (floorElevationOffset >= 0) {
        uniqueFloorOffsets[floorElevationOffset] = true;
      }

      uiniqueCeilingOffsets[room.ceilingHeightOffset] = true;
    }
  }

  return {
    floorOffsets: sortUniqueOffsets(uniqueFloorOffsets),
    ceilingOffsets: sortUniqueOffsets(uiniqueCeilingOffsets),
  };
}

function isValidRoomForAddingOffsets(room: Room, building: Building, viewPlane: Plane): boolean {
  const { walls, levels, roofSystems: roofs, roomFloorAssemblies } = building;

  // discard exterior rooms
  if (!isInteriorRoomEnclosure[room.attributes.roomEnclosure]) {
    return false;
  }

  const {
    level: { id: levelId },
  } = room;

  const level = levels[levelId];
  const wallIndex = walls[levelId].wallIndex;
  const roomWalls = wallIndex.wallsInRoom(room);
  const roomFloorAssembly = roomFloorAssemblies[room.id];

  // Fudge to make the behavior match the previous behavior
  // that was using reversed view planes for the left and right views.
  // TODO: Remove the view plane negation
  const viewPlaneLeftOrRight = Math.abs(viewPlane.uY) > Math.abs(viewPlane.uX);
  const [viewX, viewY] = viewPlaneLeftOrRight ? vec3Negate(...planeNormal(viewPlane)) : planeNormal(viewPlane);
  const { uX, uY } = viewPlaneLeftOrRight ? { uX: -viewPlane.uX, uY: -viewPlane.uY } : viewPlane;

  // look for a valid wall
  for (const wall of roomWalls) {
    const isExteriorWall = checkWallRooms(wall, (r) => {
      return !r || !isWalledRoomEnclosure[r.attributes.roomEnclosure];
    });
    // only solid walls and with an exterior room on the other side are valid
    if (wall.wallType !== WallType.SOLID || !isExteriorWall) {
      continue;
    }

    const path = getWallPath(wall);
    const { leftEdge, rightEdge } = solidWallSubstrateLayer(wall);
    const { leftRoom, startX, endX, startY, endY } = path;
    const outsideIsLeft = !leftRoom || !isWalledRoomEnclosure[leftRoom.attributes.roomEnclosure];
    const refEdge = outsideIsLeft ? leftEdge : rightEdge;

    const [nx, ny] = line2Normal(startX, startY, endX, endY, outsideIsLeft);

    // only walls facing to the camera or to the right of the camera are valid
    // TODO: Imporve this (maybe adding elevation dimensions on both sides)
    const viewToEdgeDot = vec2Dot(viewX, viewY, nx, ny);
    const viewSideToEdgeDot = vec2Dot(-uX, -uY, nx, ny);
    if (viewToEdgeDot < DIST_EPSILON && viewSideToEdgeDot < DIST_EPSILON) {
      continue;
    }

    let isWallOutside = true;
    for (const roofId in roofs) {
      const roof = roofs[roofId];

      const { insideBSP } = roof.clipContext;

      // only walls outside a roof volume are valid
      const [x, y] = polyLine2EdgeEval(refEdge, 0, 0.5, true);
      const z = floorAssemblyTop(roomFloorAssembly, level);
      if (isPointInsideBSP(insideBSP, x, y, z, true, MULTIGABLE_BSP_EPSILON)) {
        isWallOutside = false;
        break;
      }
    }

    if (isWallOutside) {
      return true;
    }
  }

  return false;
}

function prepareElevationNoteForPlacement({
  viewX,
  viewY,
  nx,
  ny,
  elevationNote,
  finalMatrix,
  x,
  y,
  z,
  mirror,
}: {
  viewX: number;
  viewY: number;
  nx: number;
  ny: number;
  elevationNote: ElevationNote;
  finalMatrix: Mat4;
  x: number;
  y: number;
  z: number;
  mirror: boolean;
}): ManualEntityAnnotationInfo | undefined {
  const viewToEdgeDot = vec2Dot(viewX, viewY, -nx, -ny);
  if (viewToEdgeDot < DIST_EPSILON) {
    return undefined;
  }

  // rx, ry is a rotated direction for the leader
  const [rx, ry] = vec2Rotate(1, 0, elevationNoteMirror(elevationNote.elevationNote, mirror));
  const [screenX, screenY] = mat4Dot(finalMatrix, x, y, z);

  return { type: "MANUAL", elevationNote, rx, ry, screenX, screenY };
}

function getElevationManualNotes({
  building,
  viewX,
  viewY,
  finalMatrix,
  mirror,
}: {
  building: Building;
  viewX: number;
  viewY: number;
  finalMatrix: Mat4;
  mirror: boolean;
}): ManualEntityAnnotationInfo[] {
  const { elevationNotes } = building;
  const notes: ManualEntityAnnotationInfo[] = [];
  for (const elevationNoteId in elevationNotes) {
    const { elevationNote, x, y, z, nx, ny, isExteriorWall } = elevationNotes[elevationNoteId];
    if (!isExteriorWall || elevationNote.disabled) {
      continue;
    }

    assertIsNumber(nx);
    assertIsNumber(ny);

    const n = prepareElevationNoteForPlacement({
      viewX,
      viewY,
      nx,
      ny,
      elevationNote: elevationNotes[elevationNoteId],
      finalMatrix,
      x,
      y,
      z,
      mirror,
    });

    if (n) {
      notes.push(n);
    }
  }

  return notes;
}

function addManualElevationNotesInPlace({
  notes,
  primitivesOut,
}: {
  notes: ManualEntityAnnotationInfo[];
  primitivesOut: ElevationAnnotationGeometryBuilder;
}) {
  notes.forEach((note) => {
    const { rx, ry, screenX, screenY } = note;
    const [pX, pY] = primitivesOut.ptToPaperSpace(screenX, screenY);
    const elevNote = generateElevNoteSheetGeometries({
      x: pX,
      y: pY,
      text: note.elevationNote.elevationNote.text,
      leaderVector: [rx, ry],
    });
    for (const geom of elevNote) {
      if (geom.type === SheetGeometryType.POLYLINE && !geom.fill) {
        primitivesOut.addPaperSpacePolylineGeometryWithCollision(geom, SheetGeometryCategory.MATERIAL_TAGS, 10);
      } else {
        primitivesOut.addPaperSpaceGeometry(geom, SheetGeometryCategory.MATERIAL_TAGS, 10, true);
      }
    }
  });
}

function labelAndDimensionRoofSlopes(
  { roofAnnotationSettings, primitivesOut, viewPlane, dimensionPrecision }: RoofAnnotationOptions,
  levelRoofFaces: Table<RoofFace[]>,
  dormerLevelRoofFaces: Table<RoofFace[]>,
) {
  const { roofSlopeTagInfos: uniqueRoofPlaneTags, roofEdgeLines } = extractUniqueRoofPlaneTags(
    viewPlane,
    levelRoofFaces,
    dormerLevelRoofFaces,
    roofAnnotationSettings,
  );

  for (const { slope, flip, pos } of uniqueRoofPlaneTags) {
    const [annoX, annoY] = pos;
    const [pX, pY] = primitivesOut.ptToPaperSpace(annoX, annoY);
    const slopeTagSheetGeometries = generateElevSlopeTagSheetGeometry({
      x: pX,
      y: pY - sheetPointsToInches(sheetPointsNew(6)),
      slope,
      flipHorizontal: flip,
    });
    for (const geom of slopeTagSheetGeometries) {
      primitivesOut.addPaperSpaceGeometry(geom.geometry, SheetGeometryCategory.ROOF_SLOPE_SYMBOL, geom.weight);
    }
  }

  dimensionRoofSlopes(primitivesOut, roofEdgeLines, dimensionPrecision);
}

const ROOF_DIM_OFFSET = sheetPointsNew(12);
const ROOF_EDGE_DIM_VERTICAL_OFFSET = sheetPointsNew(12);
const ROOF_EDGE_DIM_HORIZONTAL_OFFSET = sheetPointsNew(12);

// NOTE: this creates roof width dimensions
function dimensionRoofSlopes(
  primitivesOut: ElevationAnnotationGeometryBuilder,
  roofEdgeLines: RoofEdgeLine[],
  dimensionPrecision?: DimensionPrecision,
) {
  const groupedParallelLinesArray = groupRoofEdgeLines(roofEdgeLines);
  const joinedLines = joinRoofEdgesWithOverlappingIntervals(groupedParallelLinesArray);

  const horizDimLeaderLines: PolyLine2[] = [];
  const vertDimLeaderLines: PolyLine2[] = [];
  for (const { line, dimType } of joinedLines) {
    const a = polyLine2AABB2(line);
    const [sX, sY] = polyLine2Start(line);
    const [eX, eY] = polyLine2End(line);

    let dimVal: number;
    let x1: SheetInches;
    let y1: SheetInches;
    let x2: SheetInches;
    let y2: SheetInches;

    if (dimType === RoofEdgeDimType.HORIZONTAL) {
      horizDimLeaderLines.push(polyLine2ByTwoPoints(eX, eY, eX, a.maxY + ROOF_EDGE_DIM_VERTICAL_OFFSET));
      horizDimLeaderLines.push(polyLine2ByTwoPoints(sX, sY, sX, a.maxY + ROOF_EDGE_DIM_VERTICAL_OFFSET));

      dimVal = a.maxX - a.minX;
      x1 = primitivesOut.xToPaperSpace(a.minX);
      x2 = primitivesOut.xToPaperSpace(a.maxX);
      y1 = primitivesOut.yToPaperSpace(a.maxY + ROOF_EDGE_DIM_VERTICAL_OFFSET);
      y2 = primitivesOut.yToPaperSpace(a.maxY + ROOF_EDGE_DIM_VERTICAL_OFFSET);
    } else {
      vertDimLeaderLines.push(polyLine2ByTwoPoints(sX, sY, a.maxX + ROOF_EDGE_DIM_HORIZONTAL_OFFSET, sY));
      vertDimLeaderLines.push(polyLine2ByTwoPoints(eX, eY, a.maxX + ROOF_EDGE_DIM_HORIZONTAL_OFFSET, eY));

      dimVal = a.maxY - a.minY;
      x1 = primitivesOut.xToPaperSpace(a.maxX + ROOF_EDGE_DIM_HORIZONTAL_OFFSET);
      x2 = primitivesOut.xToPaperSpace(a.maxX + ROOF_EDGE_DIM_HORIZONTAL_OFFSET);
      y1 = primitivesOut.yToPaperSpace(a.maxY);
      y2 = primitivesOut.yToPaperSpace(a.minY);
    }

    if (Math.round(dimVal) === 0) {
      continue;
    }

    const dimtags = generateEdgeDimTag({
      x1,
      x2,
      y1,
      y2,
      dimVal,
      dimensionPrecision,
    });
    for (const dimtag of dimtags) {
      if (dimType === RoofEdgeDimType.HORIZONTAL) {
        primitivesOut.addPaperSpaceGeometry(dimtag, SheetGeometryCategory.ROOF_WIDTH_DIMENSIONS);
      } else {
        primitivesOut.addPaperSpaceGeometry(dimtag, SheetGeometryCategory.ROOF_TRUNCATION_HEIGHT_DIMENSIONS);
      }
    }
  }

  if (horizDimLeaderLines.length > 0) {
    primitivesOut.addWorldSpaceGeometryWithCollision(
      sheetPolylineGeometryNew({
        paths: horizDimLeaderLines,
        linewidth: DIM_LW,
        fill: false,
        strokeColor: colors.black,
        dash: false,
      }),
      SheetGeometryCategory.ROOF_WIDTH_DIMENSIONS,
    );
  }

  if (vertDimLeaderLines.length > 0) {
    primitivesOut.addWorldSpaceGeometryWithCollision(
      sheetPolylineGeometryNew({
        paths: vertDimLeaderLines,
        linewidth: DIM_LW,
        fill: false,
        strokeColor: colors.black,
        dash: false,
      }),
      SheetGeometryCategory.ROOF_TRUNCATION_HEIGHT_DIMENSIONS,
    );
  }
}

interface RoofDimensionContext {
  offset: number;
  baseX: number;
  prevX: number;
}

function roofDimensionContextNew(roofsAABB: AABB2, viewPlane: Plane, offset: number): RoofDimensionContext {
  const fullPerimeter = polyLine3OnPlane(polyLine2FromAABB2(roofsAABB), planeNewXY());
  const fullPerimeterProjected = planeProjectPolyLine3(viewPlane, fullPerimeter);
  const aabb = polyLine2AABB2(fullPerimeterProjected);

  return {
    offset,
    baseX: aabb.minX,
    prevX: aabb.minX,
  };
}

interface DimensionRoofOptions {
  frontEdge: PolyLine3;
  baseHeight: number;
  peakHeight: number;
  roofDimensionContext: RoofDimensionContext;
  viewPlane: Plane;
  primitivesOut: ElevationAnnotationGeometryBuilder;
  isDormer: boolean;
  dimensionPrecision?: DimensionPrecision;
  getAnnotationInstanceSetting: (key: keyof RoofElevationPlanSettings) => boolean;
}

function dimensionRoof({
  frontEdge,
  baseHeight,
  peakHeight,
  roofDimensionContext,
  viewPlane,
  primitivesOut,
  isDormer,
  dimensionPrecision,
  getAnnotationInstanceSetting,
}: DimensionRoofOptions) {
  const roofDimLeaderLines: PolyLine2[] = [];
  const widthDimLeaderLines: PolyLine2[] = [];
  const addRoofDim = (
    line: PolyLine2,
    category: SheetGeometryCategory,
    primitivesOutInternal: ElevationAnnotationGeometryBuilder,
    dimLeaderLines: PolyLine2[],
    offset: number = 0,
    isVertical: boolean = false,
  ) => {
    const a = polyLine2AABB2(line);

    const [sX, sY] = polyLine2Start(line);
    const [eX, eY] = polyLine2End(line);

    let dimVal: number;
    let x1: SheetInches;
    let y1: SheetInches;
    let x2: SheetInches;
    let y2: SheetInches;

    if (isVertical) {
      dimVal = a.maxY - a.minY;

      x1 = primitivesOut.xToPaperSpace(a.maxX + offset);
      y1 = primitivesOut.yToPaperSpace(a.maxY);

      x2 = primitivesOut.xToPaperSpace(a.maxX + offset);
      y2 = primitivesOut.yToPaperSpace(a.minY);

      dimLeaderLines.push(polyLine2ByTwoPoints(sX, sY, a.maxX + offset, sY));
      dimLeaderLines.push(polyLine2ByTwoPoints(eX, eY, a.maxX + offset, eY));
    } else {
      dimVal = a.maxX - a.minX;

      x1 = primitivesOut.xToPaperSpace(a.minX);
      y1 = primitivesOut.yToPaperSpace(a.maxY + offset);

      x2 = primitivesOut.xToPaperSpace(a.maxX);
      y2 = primitivesOut.yToPaperSpace(a.maxY + offset);

      dimLeaderLines.push(polyLine2ByTwoPoints(eX, eY, eX, a.maxY + offset));
      dimLeaderLines.push(polyLine2ByTwoPoints(sX, sY, sX, a.maxY + offset));
    }

    const dimtags = generateEdgeDimTag({
      x1,
      x2,
      y1,
      y2,
      dimVal,
      dimensionPrecision,
    });
    for (const dimtag of dimtags) {
      primitivesOutInternal.addPaperSpaceGeometry(dimtag, category);
    }
  };

  const [uX, uY, uZ] = polyLine3EdgeTangent(frontEdge, 0);
  const [oX, oY, oZ] = polyLine3StartPoint(frontEdge);
  const frontPlane = planeNew(oX, oY, oZ, uX, uY, uZ, 0, 0, 1);

  const [normalX, normalY, normalZ] = planeNormal(frontPlane);
  const [viewNormalX, vierNormalY, viewNormalZ] = planeNormal(viewPlane);

  if (vec3Dot(normalX, normalY, normalZ, viewNormalX, vierNormalY, viewNormalZ) < ONE_MINUS_DIST_EPSILON) {
    return;
  }

  const roofBaseEdge = polyLine3Move(polyLine3Copy(frontEdge), 0, 0, baseHeight);
  const roofTopEdge = polyLine3Move(polyLine3Copy(frontEdge), 0, 0, peakHeight);
  const bottomLine = planeProjectPolyLine3(viewPlane, frontEdge);
  const baseRoofLine = planeProjectPolyLine3(viewPlane, roofBaseEdge);
  const topRoofine = planeProjectPolyLine3(viewPlane, roofTopEdge);

  const [sX, sY] = polyLine2End(bottomLine);
  const [eX, eY] = polyLine2End(baseRoofLine);
  const [mX, mY] = polyLine2EdgeEval(topRoofine, 0, 0.5, true);

  const heightLine = polyLine2ByTwoPoints(sX, sY, eX, eY);

  let addHorizontalDimension = true;
  let addVerticalDimension = true;
  let addHorizontalPlacementDims = true;

  if (isDormer) {
    addVerticalDimension = getAnnotationInstanceSetting("showDormerWallFramingHeightDimensions");
    addHorizontalDimension = getAnnotationInstanceSetting("showDormerWallFramingWidthDimensions");
    addHorizontalPlacementDims = getAnnotationInstanceSetting("showDormerHorizontalPlacementDims");
  } else {
    addHorizontalDimension = false;
    addVerticalDimension = false;
    addHorizontalPlacementDims = getAnnotationInstanceSetting("showRoofHorizontalPlacementDims");
  }

  if (addHorizontalDimension) {
    addRoofDim(
      bottomLine,
      SheetGeometryCategory.ROOF_DIM_ANNOTATION,
      primitivesOut,
      roofDimLeaderLines,
      -2 * ROOF_DIM_OFFSET,
    );
  }

  if (addVerticalDimension) {
    addRoofDim(
      heightLine,
      SheetGeometryCategory.ROOF_DIM_ANNOTATION,
      primitivesOut,
      roofDimLeaderLines,
      ROOF_DIM_OFFSET,
      true,
    );
  }

  if (addHorizontalPlacementDims) {
    const { baseX, prevX, offset } = roofDimensionContext;

    const placementLine = polyLine2ByTwoPoints(prevX, mY, mX, mY);
    addRoofDim(
      placementLine,
      SheetGeometryCategory.ROOF_HORIZONTAL_PLACEMENT_DIMENSIONS,
      primitivesOut,
      widthDimLeaderLines,
      2 * ROOF_DIM_OFFSET,
    );

    if (Math.abs(prevX - baseX) > DIST_EPSILON) {
      const placementLine = polyLine2ByTwoPoints(baseX, mY, mX, mY);
      addRoofDim(
        placementLine,
        SheetGeometryCategory.ROOF_HORIZONTAL_PLACEMENT_DIMENSIONS,
        primitivesOut,
        widthDimLeaderLines,
        offset,
      );
    }

    roofDimensionContext.offset += ROOF_DIM_OFFSET;
    roofDimensionContext.prevX = mX;
  }

  if (roofDimLeaderLines.length > 0) {
    primitivesOut.addWorldSpaceGeometryWithCollision(
      sheetPolylineGeometryNew({
        paths: roofDimLeaderLines,
        linewidth: DIM_LW,
        fill: false,
        strokeColor: colors.black,
        dash: false,
      }),
      SheetGeometryCategory.ROOF_DIM_ANNOTATION,
    );
  }

  if (widthDimLeaderLines.length > 0) {
    primitivesOut.addWorldSpaceGeometryWithCollision(
      sheetPolylineGeometryNew({
        paths: widthDimLeaderLines,
        linewidth: DIM_LW,
        fill: false,
        strokeColor: colors.black,
        dash: false,
      }),
      SheetGeometryCategory.ROOF_HORIZONTAL_PLACEMENT_DIMENSIONS,
    );
  }
}

interface RoofAnnotationOptions {
  roofAnnotationSettings: RoofAnnotationSettings;
  dimensionPrecision?: DimensionPrecision;
  primitivesOut: ElevationAnnotationGeometryBuilder;
  viewPlane: Plane;
  building: Building;
}

function dimensionRoofs({
  roofAnnotationSettings,
  dimensionPrecision,
  primitivesOut,
  viewPlane,
  building,
}: RoofAnnotationOptions) {
  const { roofSystems: roofs, dormers } = building;

  const addPointToAABB = (aabb: AABB2, x: number, y: number, z: number) => {
    const [px, py] = planeProject(viewPlane, x, y, z);
    aabb2AddPoint(aabb, px, py);
  };

  // get the aabb for all the roofs
  let roofsAabb3 = aabb3New(
    Number.MAX_VALUE,
    Number.MAX_VALUE,
    Number.MAX_VALUE,
    -Number.MAX_VALUE,
    -Number.MAX_VALUE,
    -Number.MAX_VALUE,
  );
  for (const roofId in roofs) {
    const roof = roofs[roofId];

    const { roofs: subRoofs } = roof;
    for (const subRoofId in subRoofs) {
      const { roofTopFaces } = subRoofs[subRoofId];

      for (const roofTopFace of roofTopFaces) {
        roofsAabb3 = aabb3Union(roofsAabb3, faceAABB3(roofTopFace.face));
      }
    }
  }

  // project the aabb into the view plane
  const roofsAabb = aabb2New(Number.MAX_VALUE, Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);
  const { minX, minY, minZ, maxX, maxY, maxZ } = roofsAabb3;
  addPointToAABB(roofsAabb, minX, minY, minZ);
  addPointToAABB(roofsAabb, minX, minY, maxZ);
  addPointToAABB(roofsAabb, minX, maxY, minZ);
  addPointToAABB(roofsAabb, minX, maxY, maxZ);
  addPointToAABB(roofsAabb, maxX, minY, minZ);
  addPointToAABB(roofsAabb, maxX, minY, maxZ);
  addPointToAABB(roofsAabb, maxX, maxY, minZ);
  addPointToAABB(roofsAabb, maxX, maxY, maxZ);

  // iterate on sorted dormers
  const sortedSubRoofDormers = Object.values(dormers).sort((a, b) => {
    const { frontEdge: frontEdgeA } = a;
    const { frontEdge: frontEdgeB } = b;

    const [ax] = planeProject(viewPlane, frontEdgeA[0], frontEdgeA[1], frontEdgeA[2]);
    const [bx] = planeProject(viewPlane, frontEdgeB[0], frontEdgeB[1], frontEdgeB[2]);

    return ax - bx;
  });
  const dormerDimensionContext = roofDimensionContextNew(roofsAabb, viewPlane, 2 * ROOF_DIM_OFFSET);
  for (const {
    frontEdge: dormerFrontEdge,
    frontHeight,
    totalHeight,
    hostRoofSystemId,
    subRoofId,
  } of sortedSubRoofDormers) {
    const subRoof = roofs[hostRoofSystemId].roofs[subRoofId];

    if (!subRoof) {
      continue;
    }
    const { roofElevationPlanSettings } = subRoof;

    const getAnnotationInstanceSetting = (key: keyof RoofElevationPlanSettings) =>
      evalRoofAnnotationSetting(roofAnnotationSettings, roofElevationPlanSettings, key);

    dimensionRoof({
      frontEdge: dormerFrontEdge,
      baseHeight: frontHeight,
      peakHeight: totalHeight,
      roofDimensionContext: dormerDimensionContext,
      viewPlane,
      primitivesOut,
      isDormer: true,
      dimensionPrecision,
      getAnnotationInstanceSetting,
    });
  }
  for (const roofId in roofs) {
    const roof = roofs[roofId];

    const { roofs: subRoofs } = roof;
    for (const subRoofId in subRoofs) {
      const subRoof = subRoofs[subRoofId];

      if (subRoof.isDormer) {
        continue;
      }
      const { roofVolume, roofType, rails, roofElevationPlanSettings } = subRoof;
      const { baseHeight, peakHeight, startFaces, endFaces } = roofVolume;
      const { leftRail, rightRail } = rails;
      const { face: startFrontFace } = startFaces;
      const { face: endFrontFace } = endFaces;

      let frontEdge: PolyLine3 | null = null;
      if (roofType === GableRoofType.LEFT_SIDE_SHED_ROOF) {
        frontEdge = polyLine3Reverse(polyLine3Copy(leftRail));
      } else if (roofType === GableRoofType.RIGHT_SIDE_SHED_ROOF) {
        frontEdge = rightRail;
      } else if (startFrontFace) {
        frontEdge = polyLine3ByTwoPoints(
          leftRail[0],
          leftRail[1],
          leftRail[2],
          rightRail[0],
          rightRail[1],
          rightRail[2],
        );
      } else if (endFrontFace) {
        frontEdge = polyLine3ByTwoPoints(
          rightRail[3],
          rightRail[4],
          rightRail[5],
          leftRail[3],
          leftRail[4],
          leftRail[5],
        );
      }

      if (frontEdge) {
        const roofDimensionContext = roofDimensionContextNew(roofsAabb, viewPlane, 2 * ROOF_DIM_OFFSET);

        const getAnnotationInstanceSetting = (key: keyof RoofElevationPlanSettings) =>
          evalRoofAnnotationSetting(roofAnnotationSettings, roofElevationPlanSettings, key);

        dimensionRoof({
          frontEdge,
          baseHeight: baseHeight,
          peakHeight: peakHeight - baseHeight,
          roofDimensionContext,
          viewPlane,
          isDormer: false,
          primitivesOut,
          dimensionPrecision,
          getAnnotationInstanceSetting,
        });
      }
    }
  }
}

function dimensionDormerWindows({ building, roofAnnotationSettings, primitivesOut, viewPlane }: RoofAnnotationOptions) {
  const { roofSystems: roofs, dormers, levels } = building;

  for (const dormerId in dormers) {
    const { frontEdge, windows, hostRoofSystemId, subRoofId, levelId } = dormers[dormerId];

    const subRoof = roofs[hostRoofSystemId].roofs[subRoofId];

    if (!subRoof) {
      continue;
    }
    const { roofElevationPlanSettings } = subRoof;

    if (
      !evalRoofAnnotationSetting(
        roofAnnotationSettings,
        roofElevationPlanSettings,
        "showDormerWindowVerticalDimensions",
      )
    ) {
      continue;
    }

    const level = levels[levelId];
    for (const windowId in windows) {
      const window = windows[windowId];
      const startLine = planeProjectPolyLine3(planeNewXY(), frontEdge);
      dimensionGableEndOrDormerWindow(
        window,
        startLine,
        frontEdge[2],
        viewPlane,
        SheetGeometryCategory.DORMER_WINDOW_VERTICAL_DIMENSIONS,
        primitivesOut,
        level.floorElevation + level.topOfWall,
        true,
      );
    }
  }
}

function dimensionGableEndWindows({
  building,
  roofAnnotationSettings,
  primitivesOut,
  viewPlane,
}: RoofAnnotationOptions) {
  const { roofSystems: roofs } = building;

  for (const roofId in roofs) {
    const roof = roofs[roofId];

    const { roofs: subRoofs } = roof;

    for (const subRoofId in subRoofs) {
      const subRoof = subRoofs[subRoofId];
      const { isDormer, roofElevationPlanSettings } = subRoof;

      const annotationSettingKey = isDormer ? "showDormerGableWindowVerticalDims" : "showRoofGableWindowVerticalDims";

      if (!evalRoofAnnotationSetting(roofAnnotationSettings, roofElevationPlanSettings, annotationSettingKey)) {
        continue;
      }

      dimensionSubRoofGableEndWindows(
        subRoof,
        viewPlane,
        SheetGeometryCategory.ROOF_GABLE_WINDOW_VERTICAL_DIMENSIONS,
        primitivesOut,
      );
    }
  }
}

function tagGableEndWindows({ building, roofAnnotationSettings, primitivesOut, viewPlane }: RoofAnnotationOptions) {
  const { roofSystems: roofs } = building;

  for (const roofId in roofs) {
    const roof = roofs[roofId];

    const { roofs: subRoofs } = roof;

    for (const subRoofId in subRoofs) {
      const subRoof = subRoofs[subRoofId];
      const { isDormer, roofElevationPlanSettings } = subRoof;

      const annotationSettingKey = isDormer ? "showDormerGableWindowReportedName" : "showRoofGableWindowReportedName";

      if (!evalRoofAnnotationSetting(roofAnnotationSettings, roofElevationPlanSettings, annotationSettingKey)) {
        continue;
      }

      tagSubRoofGableEndWindows(subRoof, viewPlane, primitivesOut);
    }
  }
}

function tagDormerWindows({ building, roofAnnotationSettings, primitivesOut, viewPlane }: RoofAnnotationOptions) {
  const { roofSystems: roofs, dormers } = building;

  for (const dormerId in dormers) {
    const { frontEdge, windows, hostRoofSystemId, subRoofId } = dormers[dormerId];

    const subRoof = roofs[hostRoofSystemId].roofs[subRoofId];

    if (!subRoof) {
      continue;
    }
    const { roofElevationPlanSettings } = subRoof;

    if (!evalRoofAnnotationSetting(roofAnnotationSettings, roofElevationPlanSettings, "showDormerWindowReportedName")) {
      continue;
    }

    for (const windowId in windows) {
      const window = windows[windowId];
      const startLine = planeProjectPolyLine3(planeNewXY(), frontEdge);
      tagGableEndOrDormerWindow(
        window,
        startLine,
        viewPlane,
        SheetGeometryCategory.DORMER_WINDOW_TAGS,
        primitivesOut,
        frontEdge[2],
        true,
      );
    }
  }
}

function dimensionSubRoofGableEndWindows(
  subRoof: GeneratedRoof,
  viewPlane: Plane,
  category: SheetGeometryCategory,
  primitivesOut: ElevationAnnotationGeometryBuilder,
) {
  const {
    roofVolume: { baseHeight, peakHeight },
    startWindows,
    endWindows,
    wallEdges: { startLine, endLine },
  } = subRoof;

  if (startLine) {
    for (const windowId in startWindows) {
      const window = startWindows[windowId];
      dimensionGableEndOrDormerWindow(window, startLine, baseHeight, viewPlane, category, primitivesOut, peakHeight);
    }
  }

  if (endLine) {
    for (const windowId in endWindows) {
      const window = endWindows[windowId];
      dimensionGableEndOrDormerWindow(window, endLine, baseHeight, viewPlane, category, primitivesOut, peakHeight);
    }
  }
}

function tagSubRoofGableEndWindows(
  subRoof: GeneratedRoof,
  viewPlane: Plane,
  primitivesOut: ElevationAnnotationGeometryBuilder,
) {
  const {
    startWindows,
    endWindows,
    leftEaveWindows = {},
    rightEaveWindows = {},
    wallEdges: { startLine, endLine, leftEaveLine, rightEaveLine },
    roofVolume: { baseHeight, ceilingHeight },
  } = subRoof;

  for (const windowId in startWindows) {
    const window = startWindows[windowId];
    tagGableEndOrDormerWindow(
      window,
      startLine,
      viewPlane,
      SheetGeometryCategory.ROOF_GABLE_WINDOW_TAGS,
      primitivesOut,
      baseHeight,
    );
  }

  for (const windowId in endWindows) {
    const window = endWindows[windowId];
    tagGableEndOrDormerWindow(
      window,
      endLine,
      viewPlane,
      SheetGeometryCategory.ROOF_GABLE_WINDOW_TAGS,
      primitivesOut,
      baseHeight,
    );
  }

  for (const windowId in leftEaveWindows) {
    const window = leftEaveWindows[windowId];
    tagGableEndOrDormerWindow(
      window,
      leftEaveLine,
      viewPlane,
      SheetGeometryCategory.ROOF_GABLE_WINDOW_TAGS,
      primitivesOut,
      // Eave wall windows use ceiling height as the wall base.
      ceilingHeight,
    );
  }

  for (const windowId in rightEaveWindows) {
    const window = rightEaveWindows[windowId];
    tagGableEndOrDormerWindow(
      window,
      rightEaveLine,
      viewPlane,
      SheetGeometryCategory.ROOF_GABLE_WINDOW_TAGS,
      primitivesOut,
      ceilingHeight,
    );
  }
}

function dimensionGableEndOrDormerWindow(
  w: GeneratedWindowPlacement,
  startLine: PolyLine2,
  baseHeight: number,
  viewPlane: Plane,
  category: SheetGeometryCategory,
  primitivesOut: ElevationAnnotationGeometryBuilder,
  dimDatum: number,
  isDormer: boolean = false,
) {
  if (!startLine) {
    return;
  }

  const [windowNormalX, windowNormalY] = polyLine2EdgeNormal(startLine, 0);
  const [viewNormalX, viewNormalY] = planeNormal(viewPlane);

  // window is oriented to view
  if (vec2Dot(viewNormalX, viewNormalY, windowNormalX, windowNormalY) < ONE_MINUS_DIST_EPSILON) {
    return;
  }

  const {
    param: edgeParam,
    paramNormalized: edgeParamNormalized,
    descriptor: { width },
  } = w;
  const height = getWindowHeight(w.descriptor);

  if (height < DIST_EPSILON || width < DIST_EPSILON) {
    return;
  }

  const [windowPtX, windowPtY] = polyLine2EdgeEval(
    startLine,
    0,
    isDormer ? edgeParam : 1 - edgeParam,
    edgeParamNormalized,
  );

  const bottomOfWindow = baseHeight + windowHeadHeight(w) - height;

  const [centerX] = planeProject(viewPlane, windowPtX, windowPtY, 0);

  const dimBase = bottomOfWindow + height;
  let dimTagX = centerX;

  if (isDormer) {
    const [dormerEndX, dormerEndY] = polyLine2End(startLine);
    const [dimX] = planeProject(viewPlane, dormerEndX, dormerEndY, 0);
    const dimLeaderLines = [polyLine2ByTwoPoints(centerX, dimBase, dimX, dimBase)];
    primitivesOut.addWorldSpaceGeometryWithCollision(
      sheetPolylineGeometryNew({
        paths: dimLeaderLines,
        linewidth: DIM_LW,
        fill: false,
        strokeColor: colors.black,
        dash: false,
      }),
      category,
    );
    dimTagX = dimX;
  }

  const dimtags = generateEdgeDimTag({
    x1: primitivesOut.xToPaperSpace(dimTagX),
    x2: primitivesOut.xToPaperSpace(dimTagX),
    y1: primitivesOut.yToPaperSpace(dimBase),
    y2: primitivesOut.yToPaperSpace(dimDatum),
    dimVal: Math.abs(dimBase - dimDatum),
    dimensionPrecision: DimensionPrecision.PRECISION_1_4,
  });
  for (const dimtag of dimtags) {
    primitivesOut.addPaperSpaceGeometry(dimtag, category);
  }
}

function tagGableEndOrDormerWindow(
  w: GeneratedWindowPlacement,
  startLine: PolyLine2 | undefined,
  viewPlane: Plane,
  category: SheetGeometryCategory,
  primitivesOut: ElevationAnnotationGeometryBuilder,
  baseHeight: number,
  isDormer: boolean = false,
) {
  if (!startLine) {
    return;
  }

  const [windowNormalX, windowNormalY] = polyLine2EdgeNormal(startLine, 0);
  const [viewNormalX, viewNormalY] = planeNormal(viewPlane);

  // window is oriented to view
  if (vec2Dot(viewNormalX, viewNormalY, windowNormalX, windowNormalY) < ONE_MINUS_DIST_EPSILON) {
    return;
  }

  const {
    param: edgeParam,
    paramNormalized: edgeParamNormalized,
    descriptor: { width },
  } = w;
  const height = getWindowHeight(w.descriptor);

  if (height < DIST_EPSILON || width < DIST_EPSILON) {
    return;
  }

  const [windowPtX, windowPtY] = polyLine2EdgeEval(
    startLine,
    0,
    isDormer ? edgeParam : 1 - edgeParam,
    edgeParamNormalized,
  );
  const [windowTanX, windowTanY] = polyLine2EdgeTangent(startLine, 0);

  const bottomOfWindow = baseHeight + windowHeadHeight(w) - height;
  const halfWidth = width / 2;

  const [windowHalfWidthX, windowHalfWidthY] = vec2Scale(windowTanX, windowTanY, halfWidth);
  const [windowLeftX, windowLeftY] = [windowPtX - windowHalfWidthX, windowPtY - windowHalfWidthY];
  const [windowRightX, windowRightY] = [windowPtX + windowHalfWidthX, windowPtY + windowHalfWidthY];

  const [leftX] = planeProject(viewPlane, windowLeftX, windowLeftY, 0);
  const [rightX] = planeProject(viewPlane, windowRightX, windowRightY, 0);

  const edgePoly = polyLine2ByTwoPoints(rightX, bottomOfWindow, leftX, bottomOfWindow);

  const planNote = generateAndPositionPlanNote(
    w.descriptor.reportedName ?? "Reported Name",
    edgePoly,
    0.5,
    true,
    primitivesOut,
  );
  primitivesOut.addPaperSpaceGeometry(planNote, category);
}

function getElevationLevels(building: Building) {
  let includeBasements: boolean = false;
  // add conditional below ground layers
  switch (building.settings.planset.belowGradeRenderStyle) {
    case PlanLineStyle.DASHED:
    case PlanLineStyle.FULL: {
      includeBasements = true;
      break;
    }
    case PlanLineStyle.NONE: {
      if (building.settings.planset.overlayFoundationOutlines) {
        includeBasements = true;
      }
      break;
    }
    default: {
      break;
    }
  }

  const elevationLevels = sortedLevels(building.levels).filter(
    (level: Level) => building.roomsByLevel[level.id]?.length > 0 && (includeBasements || !levelIsBasement(level)),
  );
  return elevationLevels;
}

function generateAndPositionPlanNote(
  text: string,
  edgePoly: PolyLine2,
  edgeParam: number,
  edgeParamNormalized: boolean,
  primitivesOut: ElevationAnnotationGeometryBuilder,
): SheetTextGeometry {
  const perpAdj = -8.8; // inches in world space
  const [cX, cY] = polyLine2EdgeEval(edgePoly, 0, edgeParam, edgeParamNormalized);
  const [tX, tY] = polyLine2EdgeTangent(edgePoly, 0);
  const [nX, nY] = vec2Perp(tX, tY);
  const [ncX, ncY] = ray2Eval(cX, cY, nX, nY, perpAdj);

  const x = primitivesOut.xToPaperSpace(ncX);
  const y = primitivesOut.yToPaperSpace(ncY);

  // explicit new line handling
  let textLines = text.split("\n");

  // user control for newline in studio
  if (textLines.length === 1) {
    textLines = text.split("**");
  }

  // Rejoin the split apart text lines into 1 string
  text = textLines.join("\n");

  // Deliberately unstyled: the window tag category stylesheets own font/size/color and the global
  // text style owns the outline, so View Template overrides apply. Geometry-level style would win
  // over them. Placement (alignment, anchors) is not styling and stays here.
  return sheetTextGeometryNew({
    x,
    y,
    text,
    rotation: 0,
    alignment: TextAlignment.CENTER,
    placementXAnchor: PlacementXAnchor.CENTER,
    verticalAlignment: TextVerticalAlignment.TOP,
    placementYAnchor: PlacementYAnchor.TOP,
  });
}

function getLevelOffset(
  primitivesOut: ElevationAnnotationGeometryBuilder,
  elevationAnnotationParams: ElevationAnnotationParams | undefined,
  elevationNotePlacementType: ElevationNotePlacementType,
  textScalingFactor: number,
) {
  if (elevationAnnotationParams?.levelAnnotationMethod === LevelAnnotationMethod.FIXED) {
    return ZERO_INCHES;
  }

  const labelBoundingBoxOffset = getLabelBoundingBoxOffset(
    primitivesOut,
    elevationAnnotationParams,
    elevationNotePlacementType,
    textScalingFactor,
  );

  if (elevationAnnotationParams?.levelAnnotationMethod === LevelAnnotationMethod.ELEVATION_RELATIVE) {
    return numAdd(labelBoundingBoxOffset, elevationAnnotationParams?.levelAnnotationOffset ?? ZERO_INCHES);
  }

  return labelBoundingBoxOffset;
}

function getLabelBoundingBoxOffset(
  primitivesOut: ElevationAnnotationGeometryBuilder,
  elevationAnnotationParams: ElevationAnnotationParams | undefined,
  elevationNotePlacementType: ElevationNotePlacementType,
  textScalingFactor: number,
) {
  if (elevationNotePlacementType !== ElevationNotePlacementType.BOUNDING_BOX) {
    return ZERO_INCHES;
  }

  const { boxToLabelX, textWidth } = getBoundingBoxLabelSpacing(
    primitivesOut,
    elevationAnnotationParams?.boundingBoxElbowXSpacing,
    elevationAnnotationParams?.boundingBoxLabelXSpacing,
    elevationAnnotationParams?.labelTextWidth,
  );

  const additionalPadding = sheetInchesNew(0.1);
  const scaledTextWidth = numMul(textWidth, textScalingFactor);

  return numAdd(additionalPadding, numMul(numAdd(boxToLabelX, scaledTextWidth), primitivesOut.scaleFactor));
}
